import { create } from 'zustand';

/**
 * The active DWD bulk-sync job's descriptor. Held in a module-level Zustand
 * store (not URL / component state), mirroring `bulk-job-store.ts`'s
 * single-slot shape — but for a different reason than that store exists.
 *
 * The sibling bulk-ops feature (`bulk-job-store.ts`) is a convenience: even
 * without it, `use-active-bulk-jobs-subscription.ts` can rediscover an
 * in-flight job by scanning `activeBulkJobId` on rows already present in a
 * cached `/email-accounts` list page, because a bulk op always operates on
 * accounts that already exist there.
 *
 * A DWD sync is different — the accounts it produces don't exist in ANY
 * cached list page until the sync inserts them, so there is nothing to scan.
 * Without this store, closing the sync modal (`SyncingView`) or navigating
 * away from it loses every reference to the running job, and
 * `use-active-dwd-sync-subscription.ts` would have no way to find it again —
 * the newly-synced rows would only ever appear on the next full list
 * refetch, defeating the point of live-splicing them in as batches land.
 *
 * Intentionally NOT persisted: a full page reload loses this descriptor (the
 * job keeps running server-side; there is no cancel endpoint), matching
 * `bulk-job-store.ts`'s own accepted trade-off. Single-slot: starting a
 * second DWD sync (a different workspace) stops tracking the first — its
 * rows still land correctly and surface on the next natural list refetch,
 * just not live-spliced. Not worth a multi-job store for a background-tray
 * feature.
 */
export type ActiveDwdSync = {
  workspaceId: number;
  jobId: string;
  total: number;
};

export interface DwdSyncState {
  activeSync: ActiveDwdSync | null;
  setActiveSync: (sync: ActiveDwdSync) => void;
  clearActiveSync: () => void;
}

export const useDwdSyncStore = create<DwdSyncState>()((set) => ({
  activeSync: null,
  setActiveSync: (activeSync) => set({ activeSync }),
  clearActiveSync: () => set({ activeSync: null }),
}));
